import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import UserAvatar from '../Shared/UserAvatar/UserAvatar';
import SpecialOffersTopbarDrafts from './SpecialOffersTopbarDrafts';
import { Theme, useTheme } from '../../context/ThemeContext';
import { getRoleLabel, isAdminOrOwner } from '../../utils/userRoles';
import './AppLayout.css';

interface AppLayoutProps {
  children: React.ReactNode;
  currentUser?: any | null;
  onOpenPalette: () => void;
  onOpenContractsModal: () => void;
  onOpenAdminPanel?: () => void;
}

interface NavItem {
  path: string;
  label: string;
  icon: string;
}

const DOCK_ITEM_SIZE = 48;

const navItems: NavItem[] = [
  { path: '/analytics', label: '\u0410\u043d\u0430\u043b\u0438\u0442\u0438\u043a\u0430', icon: 'fas fa-home' },
  { path: '/summaries', label: '\u0421\u0432\u043e\u0434\u043a\u0438', icon: 'fas fa-table-list' },
  { path: '/realtime-dashboard', label: 'Live-\u043f\u0430\u043d\u0435\u043b\u044c', icon: 'fas fa-tachometer-alt' },
  { path: '/registry', label: '\u041f\u0430\u043d\u0435\u043b\u044c \u0443\u043f\u0440\u0430\u0432\u043b\u0435\u043d\u0438\u044f', icon: 'fas fa-table' },
  { path: '/special-offers', label: '\u0420\u0430\u0441\u0441\u044b\u043b\u043a\u0438', icon: 'fas fa-bullhorn' },
  { path: '/autoload', label: '\u0410\u0432\u0442\u043e\u0437\u0430\u0433\u0440\u0443\u0437\u043a\u0430', icon: 'fas fa-upload' },
  { path: '/feedback', label: '\u041e\u0442\u0437\u044b\u0432\u044b', icon: 'fas fa-comment-dots' },
  { path: '/monitoring', label: '\u041c\u043e\u043d\u0438\u0442\u043e\u0440\u0438\u043d\u0433', icon: 'fas fa-chart-line' },
  { path: '/parser', label: '\u041f\u0430\u0440\u0441\u0435\u0440', icon: 'fas fa-robot' },
  { path: '/photoflow', label: '\u041a\u043e\u043d\u0432\u0435\u0440\u0442\u0435\u0440', icon: 'fas fa-image' },
  { path: '/calls', label: '\u0421\u043e\u0437\u0432\u043e\u043d\u044b', icon: 'fas fa-phone' },
  { path: '/inbox', label: 'Inbox', icon: 'fas fa-comments' },
];

const getSidebarDensityMode = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  return window.innerHeight <= 820;
};

const DockNavItem = ({
  item,
  isActive,
  mouseY,
  slotSize,
  expandedSize,
  compact,
}: {
  item: NavItem;
  isActive: boolean;
  mouseY: ReturnType<typeof useMotionValue<number>>;
  slotSize: number;
  expandedSize: number;
  compact: boolean;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);

  const distance = useTransform(mouseY, (value: number) => {
    const element = ref.current;
    if (!element || value === Infinity) {
      return Infinity;
    }

    const rect = element.getBoundingClientRect();
    return value - rect.top - rect.height / 2;
  });

  const sizeTransform = useTransform(distance, [-120, 0, 120], [slotSize, expandedSize, slotSize]);
  const size = useSpring(sizeTransform, { mass: 0.1, stiffness: 150, damping: 12 });

  return (
    <div
      ref={ref}
      className={`dock-slot ${compact ? 'compact' : ''}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{ width: slotSize, height: slotSize, zIndex: isHovered ? 40 : isActive ? 3 : 1 }}
    >
      <motion.div
        className="dock-icon-scaler"
        style={{
          width: size,
          height: size,
          position: 'absolute',
          top: '50%',
          left: 0,
          zIndex: isHovered ? 41 : isActive ? 4 : 1,
          y: '-50%',
        }}
      >
        <Link
          to={item.path}
          className={`app-pro-menu-item dock-item ${isActive ? 'active' : ''} ${compact ? 'compact' : ''}`}
          aria-label={item.label}
          style={{ width: '100%', height: '100%', margin: 0, borderRadius: '50%', position: 'relative', zIndex: isHovered ? 42 : isActive ? 5 : 1 }}
        >
          <i
            className={`${item.icon} app-pro-menu-icon`}
            style={{ fontSize: compact ? '16px' : '18px', width: 'auto', margin: 0 }}
          />
        </Link>
      </motion.div>

      <AnimatePresence>
        {isHovered && (
          <motion.div
            className="app-pro-dock-tooltip"
            initial={{ opacity: 0, x: 10, y: '-50%' }}
            animate={{ opacity: 1, x: 20, y: '-50%' }}
            exit={{ opacity: 0, x: 10, y: '-50%' }}
          >
            {item.label}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const AppLayout: React.FC<AppLayoutProps> = ({
  children,
  currentUser,
  onOpenPalette,
  onOpenContractsModal,
  onOpenAdminPanel,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const mouseY = useMotionValue(Infinity);
  const utilityMenuRef = useRef<HTMLDivElement>(null);
  const [isUtilityMenuOpen, setIsUtilityMenuOpen] = useState(false);
  const [isCompactSidebar, setIsCompactSidebar] = useState(getSidebarDensityMode);

  useEffect(() => {
    const syncSidebarDensity = () => {
      setIsCompactSidebar(getSidebarDensityMode());
    };

    window.addEventListener('resize', syncSidebarDensity);
    syncSidebarDensity();

    return () => {
      window.removeEventListener('resize', syncSidebarDensity);
    };
  }, []);

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (utilityMenuRef.current && !utilityMenuRef.current.contains(target)) {
        setIsUtilityMenuOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsUtilityMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  useEffect(() => {
    setIsUtilityMenuOpen(false);
  }, [location.pathname]);

  const handleMouseMove = (event: React.MouseEvent) => {
    mouseY.set(event.clientY);
  };

  const handleMouseLeave = () => {
    mouseY.set(Infinity);
  };

  const handleOpenClickUp = () => {
    setIsUtilityMenuOpen(false);
    navigate('/clickup');
  };

  const handleOpenAiPanel = () => {
    setIsUtilityMenuOpen(false);
    onOpenPalette();
  };

  const handleOpenContracts = () => {
    setIsUtilityMenuOpen(false);
    onOpenContractsModal();
  };

  const handleOpenAdmin = () => {
    if (!onOpenAdminPanel) {
      return;
    }

    setIsUtilityMenuOpen(false);
    onOpenAdminPanel();
  };

  const handleThemeChange = (nextTheme: Theme) => {
    if (nextTheme !== theme) {
      setTheme(nextTheme);
    }
  };

  const dockSlotSize = isCompactSidebar ? 40 : DOCK_ITEM_SIZE;
  const dockExpandedSize = isCompactSidebar ? 56 : 80;

  return (
    <div className="app-pro-layout app-pro-layout-collapsed">
      <aside className={`app-pro-sidebar ${isCompactSidebar ? 'compact' : ''}`}>
        <Link to="/" className="app-pro-brand" style={{ textDecoration: 'none', color: 'inherit' }}>
          <div className={`app-pro-brand-icon ${isCompactSidebar ? 'compact' : ''}`}>
            <img src="/logo.png" alt="AVM Analytics logo" className="app-pro-brand-logo" />
          </div>
          <div className="app-pro-brand-name">AVM Analytics</div>
        </Link>

        <div className="app-pro-menu-section">
          <nav
            className={`app-pro-menu-items ${isCompactSidebar ? 'compact' : ''}`}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
          >
            {navItems.map((item) => (
              <DockNavItem
                key={item.path}
                item={item}
                isActive={location.pathname === item.path}
                mouseY={mouseY}
                slotSize={dockSlotSize}
                expandedSize={dockExpandedSize}
                compact={isCompactSidebar}
              />
            ))}
          </nav>
        </div>

        <div className={`app-pro-sidebar-bottom ${isCompactSidebar ? 'compact' : ''}`}>
          {currentUser ? (
            <Link
              to="/profile"
              className={`app-pro-user-card ${isCompactSidebar ? 'compact' : ''}`}
              aria-label="\u041f\u0440\u043e\u0444\u0438\u043b\u044c"
            >
              <div className={`app-pro-user-avatar ${isCompactSidebar ? 'compact' : ''}`}>
                <UserAvatar user={currentUser} size="small" />
              </div>
              <div className="app-pro-user-details">
                <h4>{currentUser.first_name || currentUser.username || 'User'}</h4>
                <span>{getRoleLabel(currentUser)}</span>
              </div>
            </Link>
          ) : (
            <div className={`app-pro-user-card ${isCompactSidebar ? 'compact' : ''}`}>
              <div className={`app-pro-user-avatar ${isCompactSidebar ? 'compact' : ''}`}>
                <div className="app-pro-user-avatar-placeholder">?</div>
              </div>
              <div className="app-pro-user-details">
                <h4>{'\u0413\u043e\u0441\u0442\u044c'}</h4>
                <span>{'\u041f\u043e\u043b\u044c\u0437\u043e\u0432\u0430\u0442\u0435\u043b\u044c'}</span>
              </div>
            </div>
          )}

          <div className="app-pro-utility-menu app-pro-utility-menu-sidebar" ref={utilityMenuRef}>
            <button
              type="button"
              className={`app-pro-utility-trigger ${isUtilityMenuOpen ? 'open' : ''} ${isCompactSidebar ? 'compact' : ''}`}
              aria-label="\u0421\u0438\u0441\u0442\u0435\u043c\u043d\u044b\u0435 \u0434\u0435\u0439\u0441\u0442\u0432\u0438\u044f"
              aria-expanded={isUtilityMenuOpen}
              aria-haspopup="menu"
              onClick={() => setIsUtilityMenuOpen((value) => !value)}
            >
              <span className="app-pro-utility-dots" aria-hidden="true">
                <span />
                <span />
                <span />
              </span>
            </button>

            <AnimatePresence>
              {isUtilityMenuOpen && (
                <motion.div
                  className="app-pro-utility-popover app-pro-utility-popover-sidebar"
                  initial={{ opacity: 0, x: -8, scale: 0.96 }}
                  animate={{ opacity: 1, x: 0, scale: 1 }}
                  exit={{ opacity: 0, x: -8, scale: 0.96 }}
                  transition={{ duration: 0.16, ease: [0.2, 0.9, 0.2, 1] }}
                  role="menu"
                >
                  <button type="button" className="app-pro-utility-item" onClick={handleOpenClickUp} role="menuitem">
                    <span className="app-pro-utility-item-icon" aria-hidden="true">
                      <i className="fas fa-check" />
                    </span>
                    <span className="app-pro-utility-item-copy">
                      <strong>ClickUp</strong>
                      <small>{'\u041e\u0447\u0435\u0440\u0435\u0434\u044c \u0437\u0430\u0434\u0430\u0447 \u0438 \u043a\u043e\u043d\u0442\u0440\u043e\u043b\u044c \u0438\u0441\u043f\u043e\u043b\u043d\u0435\u043d\u0438\u044f'}</small>
                    </span>
                  </button>

                  <button type="button" className="app-pro-utility-item" onClick={handleOpenAiPanel} role="menuitem">
                    <span className="app-pro-utility-item-icon" aria-hidden="true">
                      <i className="fas fa-robot" />
                    </span>
                    <span className="app-pro-utility-item-copy">
                      <strong>{'\u0418\u0418-\u043f\u0430\u043d\u0435\u043b\u044c'}</strong>
                      <small>{'\u041a\u043e\u043c\u0430\u043d\u0434\u044b, \u043f\u043e\u0438\u0441\u043a \u0438 \u0431\u044b\u0441\u0442\u0440\u044b\u0435 \u0434\u0435\u0439\u0441\u0442\u0432\u0438\u044f'}</small>
                    </span>
                  </button>

                  <button type="button" className="app-pro-utility-item" onClick={handleOpenContracts} role="menuitem">
                    <span className="app-pro-utility-item-icon" aria-hidden="true">
                      <i className="fas fa-file-signature" />
                    </span>
                    <span className="app-pro-utility-item-copy">
                      <strong>{'Генератор договоров'}</strong>
                      <small>{'Загрузка DOCX-шаблонов, поля подстановки и быстрый выпуск готового договора'}</small>
                    </span>
                  </button>

                  {isAdminOrOwner(currentUser) && onOpenAdminPanel && (
                    <button type="button" className="app-pro-utility-item" onClick={handleOpenAdmin} role="menuitem">
                      <span className="app-pro-utility-item-icon" aria-hidden="true">
                        <i className="fas fa-cog" />
                      </span>
                      <span className="app-pro-utility-item-copy">
                        <strong>{'\u0410\u0434\u043c\u0438\u043d-\u043f\u0430\u043d\u0435\u043b\u044c'}</strong>
                        <small>{'\u0421\u043b\u0443\u0436\u0435\u0431\u043d\u044b\u0435 \u043d\u0430\u0441\u0442\u0440\u043e\u0439\u043a\u0438 \u0438 \u0441\u0438\u0441\u0442\u0435\u043c\u043d\u044b\u0435 \u0434\u0435\u0439\u0441\u0442\u0432\u0438\u044f'}</small>
                      </span>
                    </button>
                  )}

                  <div className="app-pro-utility-section" role="none">
                    <div className="app-pro-utility-section-label">Оформление</div>
                    <div className="app-pro-theme-switch" role="group" aria-label="Тема интерфейса">
                      <button
                        type="button"
                        className={`app-pro-theme-option ${theme === 'dark' ? 'active' : ''}`}
                        onClick={() => handleThemeChange('dark')}
                        aria-pressed={theme === 'dark'}
                      >
                        <span className="app-pro-theme-option-icon" aria-hidden="true">
                          <i className="fas fa-moon" />
                        </span>
                        <span className="app-pro-theme-option-copy">
                          <strong>Тёмная</strong>
                          <small>Для мониторинга и плотной работы</small>
                        </span>
                      </button>

                      <button
                        type="button"
                        className={`app-pro-theme-option ${theme === 'light' ? 'active' : ''}`}
                        onClick={() => handleThemeChange('light')}
                        aria-pressed={theme === 'light'}
                      >
                        <span className="app-pro-theme-option-icon" aria-hidden="true">
                          <i className="fas fa-sun" />
                        </span>
                        <span className="app-pro-theme-option-copy">
                          <strong>Светлая</strong>
                          <small>Для реестров, обзоров и настроек</small>
                        </span>
                      </button>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </aside>

      <main className="app-pro-main">
        {location.pathname === '/special-offers' && (
          <div className="app-pro-content-tools">
            <SpecialOffersTopbarDrafts />
          </div>
        )}

        <section className="app-pro-content">{children}</section>
      </main>
    </div>
  );
};

export default AppLayout;
